import {
  LinkOutlined,
  LockOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  message,
  Modal,
  Space,
  Spin,
  Typography,
} from "antd";
import React, { useEffect, useState } from "react";
import {
  disconnectQuickBooks,
  getQuickBooksAuthUrl,
  getQuickBooksTokenWithPassword,
  getQuickBooksConnectionStatus,
  QuickBooksTokenResponse,
} from "~/services/quickbooks/quickbooks.api";

const { Title, Text, Paragraph } = Typography;

// interface QuickBooksSettings {
//   clientId: string;
//   clientSecret: string;
//   redirectUri: string;
//   environment: "sandbox" | "production";
//   autoSync: boolean;
//   syncInterval: number;
// }

interface QuickBooksConnectionStatus {
  isConnected: boolean;
  companyName?: string;
  realmId?: string;
  lastSyncTime?: string;
  tokenExpiresAt?: string;
  error?: string;
  authUrl?: string;
}

// interface SyncHistory {
//   id: string;
//   type: "invoice" | "customer" | "item";
//   status: "success" | "failed" | "pending";
//   timestamp: string;
//   details: string;
// }

export const QuickBooksIntegration: React.FC = () => {
  const [connectionStatus, setConnectionStatus] =
    useState<QuickBooksConnectionStatus | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
  const [isGettingToken, setIsGettingToken] = useState(false);
  const [oauthToken, setOauthToken] = useState<QuickBooksTokenResponse | null>(
    null
  );
  const [form] = Form.useForm();

  useEffect(() => {
    loadConnectionStatus();
  }, []);

  const loadConnectionStatus = async () => {
    try {
      setIsLoading(true);
      const status = await getQuickBooksConnectionStatus();
      setConnectionStatus(status);
    } catch (error: any) {
      message.error(error?.message || "Failed to load connection status");
      setConnectionStatus({
        isConnected: false,
        error: "Failed to load connection status",
        authUrl: "/api/quickbooks/auth/url",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      setIsConnecting(true);
      const authUrl = await getQuickBooksAuthUrl();

      // Open QuickBooks OAuth in a new window
      const authWindow = window.open(
        authUrl.authUrl,
        "quickbooks-auth",
        "width=600,height=700,scrollbars=yes,resizable=yes"
      );

      // Listen for the auth completion
      const checkAuthComplete = setInterval(() => {
        try {
          if (authWindow?.closed) {
            clearInterval(checkAuthComplete);
            // Check connection status after auth window closes
            setTimeout(() => {
              loadConnectionStatus();
              setIsConnecting(false);
            }, 1000);
          }
        } catch (error) {
          // Handle cross-origin errors
          console.log("error", error);
        }
      }, 1000);
    } catch (error: any) {
      message.error(
        error?.message || "Failed to initiate QuickBooks connection"
      );
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnectQuickBooks();
      setConnectionStatus({ isConnected: false });
      message.success("Successfully disconnected from QuickBooks");
    } catch (error: any) {
      message.error(error?.message || "Failed to disconnect from QuickBooks");
    }
  };

  const showPasswordModal = () => {
    setIsPasswordModalVisible(true);
    form.resetFields();
    setOauthToken(null);
  };

  const handlePasswordModalCancel = () => {
    setIsPasswordModalVisible(false);
    form.resetFields();
  };

  const handleGetOAuthToken = async () => {
    try {
      const values = await form.validateFields();
      setIsGettingToken(true);

      const token = await getQuickBooksTokenWithPassword(values.password);
      setOauthToken(token);
      message.success("OAuth token retrieved successfully");
    } catch (error: any) {
      if (error.errorFields) {
        return; // Form validation error
      }
      message.error(error?.message || "Failed to get OAuth token");
    } finally {
      setIsGettingToken(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading QuickBooks Integration...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
      <Title level={2}>
        <SettingOutlined style={{ marginRight: 8 }} />
        QuickBooks Integration
      </Title>
      <Paragraph>
        Manage your QuickBooks Online integration and view connection status.
      </Paragraph>

      {/* Connection Status Card */}
      <Card
        title={
          <Space>
            <LinkOutlined />
            Connection Status
          </Space>
        }
        style={{ marginBottom: 24 }}
      >
        {connectionStatus?.isConnected ? (
          <Alert
            message="QuickBooks Connected"
            description={
              <div>
                <div>
                  <strong>Company:</strong>{" "}
                  {connectionStatus.companyName || "Connected"}
                </div>
                {connectionStatus.lastSyncTime && (
                  <div>
                    <strong>Last Sync:</strong>{" "}
                    {new Date(connectionStatus.lastSyncTime).toLocaleString()}
                  </div>
                )}
                {connectionStatus.tokenExpiresAt && (
                  <div>
                    <strong>Token Expires:</strong>{" "}
                    {new Date(connectionStatus.tokenExpiresAt).toLocaleString()}
                  </div>
                )}
              </div>
            }
            type="success"
            showIcon
            action={
              <Space>
                <Button type="default" danger onClick={handleDisconnect}>
                  Disconnect
                </Button>
                <Button icon={<LockOutlined />} onClick={showPasswordModal}>
                  Get OAuth Token
                </Button>
              </Space>
            }
          />
        ) : (
          <Alert
            message="QuickBooks Not Connected"
            description={
              connectionStatus?.error ||
              "Connect to QuickBooks to enable invoice creation and synchronization."
            }
            type="warning"
            showIcon
            action={
              <Button
                type="primary"
                loading={isConnecting}
                onClick={handleConnect}
              >
                Connect to QuickBooks
              </Button>
            }
          />
        )}
      </Card>

      {/* Password Modal for OAuth Token */}
      <Modal
        title="Get OAuth Token"
        open={isPasswordModalVisible}
        onCancel={handlePasswordModalCancel}
        footer={[
          <Button key="cancel" onClick={handlePasswordModalCancel}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={isGettingToken}
            onClick={handleGetOAuthToken}
          >
            Get Token
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="password"
            label="Admin Password"
            rules={[
              { required: true, message: "Please enter the admin password" },
            ]}
          >
            <Input.Password placeholder="Enter admin password" />
          </Form.Item>

          {oauthToken && (
            <>
              <div className="bg-gray-50 p-4 rounded-md mt-4">
                <h4 className="text-lg font-medium mb-2">
                  OAuth Token Information
                </h4>
                <div className="mb-2">
                  <div className="font-medium">Access Token:</div>
                  <Input.TextArea
                    value={oauthToken.access_token}
                    readOnly
                    rows={2}
                    className="mb-2"
                  />
                  <Button
                    type="link"
                    onClick={() => {
                      navigator.clipboard.writeText(oauthToken.access_token);
                      message.success("Access token copied to clipboard");
                    }}
                    style={{ padding: 0 }}
                  >
                    Copy access token
                  </Button>
                </div>

                <div className="mb-2">
                  <div className="font-medium">Refresh Token:</div>
                  <Input.TextArea
                    value={oauthToken.refresh_token}
                    readOnly
                    rows={2}
                    className="mb-2"
                  />
                  <Button
                    type="link"
                    onClick={() => {
                      navigator.clipboard.writeText(oauthToken.refresh_token);
                      message.success("Refresh token copied to clipboard");
                    }}
                    style={{ padding: 0 }}
                  >
                    Copy refresh token
                  </Button>
                </div>

                <div className="mb-2">
                  <div className="font-medium">Realm ID:</div>
                  <Input value={oauthToken.realmId} readOnly className="mb-2" />
                  <Button
                    type="link"
                    onClick={() => {
                      navigator.clipboard.writeText(oauthToken.realmId || "");
                      message.success("Realm ID copied to clipboard");
                    }}
                    style={{ padding: 0 }}
                  >
                    Copy Realm ID
                  </Button>
                </div>

                <div>
                  <div className="font-medium">Token Details:</div>
                  <ul className="list-disc pl-5">
                    <li>Token Type: {oauthToken.token_type}</li>
                    <li>Expires In: {oauthToken.expires_in} seconds</li>
                    <li>
                      Refresh Token Expires In:{" "}
                      {oauthToken.x_refresh_token_expires_in} seconds
                    </li>
                  </ul>
                </div>
              </div>
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default QuickBooksIntegration;
